import React, { useCallback } from 'react';

import { Element } from 'common/types/tracking';
import { ImageContainerType } from '../enums';
import ResponsiveImage from '../shared/components/ResponsiveImage';
import ResponsiveVideo from '../shared/components/ResponsiveVideo';
import BigVariant from '../shared/components/variants/BigVariant';
import SlimVariant from '../shared/components/variants/SlimVariant';
import StaticBackgroundVariant from '../shared/components/variants/StaticBackgroundVariant';
import useTracking from '../shared/hooks/use-tracking';
import type { TeaserProps } from '../types';

const Teaser = ({
  bigImage,
  slimImage,
  tracking = { name: '' },
  video,
  title,
  ...props
}: TeaserProps) => {
  const { ref, isVideoVariant, mediaElement, onPromoClick, onVideoStart, onVideoComplete } =
    useTracking({
      tracking,
      video,
      title,
    });

  const onClick = useCallback(() => onPromoClick(mediaElement), [onPromoClick, mediaElement]);
  const onCtaClick = useCallback(() => onPromoClick(Element.Link), [onPromoClick]);

  if (isVideoVariant) {
    return (
      <BigVariant ref={ref} onClick={onClick} onCtaClick={onCtaClick} title={title} {...props}>
        <ResponsiveVideo
          {...props}
          type={ImageContainerType.Big}
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          video={video!}
          onClick={onClick}
          onVideoStart={onVideoStart}
          onVideoComplete={onVideoComplete}
        />
      </BigVariant>
    );
  }

  if (bigImage) {
    return (
      <BigVariant ref={ref} onClick={onClick} onCtaClick={onCtaClick} title={title} {...props}>
        <ResponsiveImage
          {...props}
          type={ImageContainerType.Big}
          image={bigImage}
          onClick={onClick}
        />
      </BigVariant>
    );
  }

  if (slimImage) {
    return (
      <SlimVariant
        {...props}
        ref={ref}
        image={slimImage}
        onClick={onClick}
        onCtaClick={onCtaClick}
        title={title}
      />
    );
  }

  return (
    <StaticBackgroundVariant
      {...props}
      ref={ref}
      onClick={onClick}
      onCtaClick={onCtaClick}
      title={title}
    />
  );
};

export default Teaser;
